import React from 'react';
import { Plus, Trash2, X, ChevronUp, ChevronDown, Calculator } from 'lucide-react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Textarea } from '../ui/textarea';
import { EDITOR_SCHEMAS } from './sectionDefs';
import { ImagePickField } from '../media/ImagePickField';

const Label = ({ children }) => <label className="text-xs font-medium text-stone-500 mb-1 block">{children}</label>;

const ImageField = ({ label, value, onChange }) => (
  <ImagePickField label={label} value={value} onChange={onChange} />
);

const BaseFieldInput = ({ field, value, onChange }) => {
  switch (field.input) {
    case 'textarea':
      return (
        <div>
          <Label>{field.label}</Label>
          <Textarea rows={field.rows || 3} value={value ?? ''} onChange={e => onChange(e.target.value)} />
        </div>
      );
    case 'number':
      return (
        <div>
          <Label>{field.label}</Label>
          <Input type="number" value={value ?? ''} onChange={e => onChange(e.target.value === '' ? '' : Number(e.target.value))} />
        </div>
      );
    case 'select':
      return (
        <div>
          <Label>{field.label}</Label>
          <select
            value={value ?? field.options?.[0]?.[0] ?? ''}
            onChange={e => onChange(e.target.value)}
            className="w-full h-10 rounded-md border border-stone-200 bg-white px-3 text-sm"
          >
            {(field.options || []).map(([v, l]) => <option key={v} value={v}>{l}</option>)}
          </select>
        </div>
      );
    case 'lines':
      return (
        <div>
          <Label>{field.label}</Label>
          <Textarea
            rows={field.rows || 4}
            value={(value || []).join('\n')}
            onChange={e => onChange(e.target.value.split('\n'))}
            onBlur={e => onChange(e.target.value.split('\n').map(s => s.trim()).filter(Boolean))}
          />
        </div>
      );
    case 'image':
      return <ImageField label={field.label} value={value} onChange={onChange} />;
    default:
      return (
        <div>
          <Label>{field.label}</Label>
          <Input value={value ?? ''} onChange={e => onChange(e.target.value)} />
        </div>
      );
  }
};

const FieldInput = ({ field, value, onChange }) => {
  if (field.input === 'objlist') return <ObjListField field={field} value={value || []} onChange={onChange} />;
  return <BaseFieldInput field={field} value={value} onChange={onChange} />;
};

const ObjListField = ({ field, value, onChange }) => {
  const items = value || [];
  const setItem = (i, key, v) => {
    const next = [...items];
    next[i] = { ...next[i], [key]: v };
    onChange(next);
  };
  const move = (i, dir) => {
    const j = i + dir;
    if (j < 0 || j >= items.length) return;
    const next = [...items];
    [next[i], next[j]] = [next[j], next[i]];
    onChange(next);
  };
  const emptyItem = Object.fromEntries(field.fields.map(f => [f.key, f.input === 'lines' ? [] : f.input === 'number' ? 0 : '']));
  return (
    <div>
      <Label>{field.label}</Label>
      <div className="space-y-3">
        {items.map((item, i) => (
          <div key={i} className="border border-stone-200 rounded-lg p-4 bg-white relative space-y-3">
            <div className="absolute top-2 right-2 flex items-center gap-0.5">
              <Button variant="ghost" size="icon" className="h-7 w-7" disabled={i === 0} onClick={() => move(i, -1)}><ChevronUp className="w-3.5 h-3.5" /></Button>
              <Button variant="ghost" size="icon" className="h-7 w-7" disabled={i === items.length - 1} onClick={() => move(i, 1)}><ChevronDown className="w-3.5 h-3.5" /></Button>
              <Button variant="ghost" size="icon" className="h-7 w-7" onClick={() => onChange(items.filter((_, j) => j !== i))}><Trash2 className="w-3.5 h-3.5 text-red-400" /></Button>
            </div>
            <p className="text-[10px] uppercase tracking-wider text-stone-400 font-semibold">{field.itemLabel || 'Item'} {i + 1}</p>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              {field.fields.map(sub => (
                <div key={sub.key} className={['textarea', 'lines', 'image'].includes(sub.input) ? 'md:col-span-2' : ''}>
                  <BaseFieldInput field={sub} value={item[sub.key]} onChange={v => setItem(i, sub.key, v)} />
                </div>
              ))}
            </div>
          </div>
        ))}
        <Button variant="outline" size="sm" onClick={() => onChange([...items, { ...emptyItem }])}>
          <Plus className="w-4 h-4 mr-1" /> Add {field.itemLabel || 'item'}
        </Button>
      </div>
    </div>
  );
};

const AutoForm = ({ schema, data, onChange }) => (
  <div className="space-y-4">
    {schema.map(field => (
      <FieldInput key={field.key} field={field} value={data[field.key]} onChange={v => onChange({ ...data, [field.key]: v })} />
    ))}
  </div>
);

const AvailabilityEditor = ({ data, onChange, page, onPageChange }) => {
  const availability = page.availability || {};
  const bookedDates = availability.booked_dates || [];
  const pricing = availability.pricing || {};
  const setAvail = (patch) => onPageChange({ ...page, availability: { ...availability, ...patch } });
  return (
    <div className="space-y-4">
      <FieldInput field={{ key: 'title', label: 'Section title', input: 'text' }} value={data.title} onChange={v => onChange({ ...data, title: v })} />
      <FieldInput field={{ key: 'intro', label: 'Intro text', input: 'textarea', rows: 3 }} value={data.intro} onChange={v => onChange({ ...data, intro: v })} />
      <div>
        <Label>Booked dates (blocked on the calendar)</Label>
        <div className="flex flex-wrap gap-2 mb-2">
          {bookedDates.map(d => (
            <span key={d} className="inline-flex items-center gap-1 bg-red-50 text-red-700 text-xs px-2 py-1 rounded-full">
              {d}
              <button onClick={() => setAvail({ booked_dates: bookedDates.filter(x => x !== d) })}><X className="w-3 h-3" /></button>
            </span>
          ))}
        </div>
        <Input type="date" data-testid="builder-booked-date-input" className="max-w-[200px]" onChange={e => {
          const v = e.target.value;
          if (v && !bookedDates.includes(v)) setAvail({ booked_dates: [...bookedDates, v].sort() });
          e.target.value = '';
        }} />
      </div>
      <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
        {[['room_price_per_night', 'Room price / night (₹)'], ['veg_plate', 'Veg plate (₹)'], ['nonveg_plate', 'Non-veg plate (₹)']].map(([k, l]) => (
          <div key={k}>
            <Label>{l}</Label>
            <Input type="number" value={pricing[k] ?? ''} onChange={e => setAvail({ pricing: { ...pricing, [k]: Number(e.target.value) } })} />
          </div>
        ))}
      </div>
      <p className="text-xs text-stone-400">Booked dates & stay pricing are shared with the browse page availability badges and the instant estimate shown when guests pick dates.</p>
    </div>
  );
};

const CostCalculatorEditor = ({ goToCalculatorTab }) => (
  <div className="bg-stone-50 border border-stone-200 rounded-lg p-5 flex items-start gap-4">
    <Calculator className="w-6 h-6 text-[#1A3626] shrink-0 mt-0.5" />
    <div>
      <p className="text-sm font-medium text-stone-900">This section shows the full Cost Calculator on the page.</p>
      <p className="text-xs text-stone-500 mt-1">
        Packages, rooms, food, add-ons, taxes and discounts are configured in this venue&apos;s <b>Cost Calculator</b> tab.
        The calculator only appears on the public page when it is switched <b>live</b> there.
      </p>
      {goToCalculatorTab && (
        <Button variant="outline" size="sm" className="mt-3" onClick={goToCalculatorTab} data-testid="goto-calculator-tab-btn">
          Open Cost Calculator settings
        </Button>
      )}
    </div>
  </div>
);

export const SectionEditor = ({ type, data, onChange, page, onPageChange, goToCalculatorTab }) => {
  if (type === 'availability_calendar') {
    return <AvailabilityEditor data={data} onChange={onChange} page={page} onPageChange={onPageChange} />;
  }
  if (type === 'cost_calculator') {
    return <CostCalculatorEditor goToCalculatorTab={goToCalculatorTab} />;
  }
  const schema = EDITOR_SCHEMAS[type];
  if (!schema) return <p className="text-sm text-stone-400">No settings for this section.</p>;
  return <AutoForm schema={schema} data={data || {}} onChange={onChange} />;
};
